/**
 * Money Goals — scroll past the selector cards after a card is chosen.
 *
 * On a Money Goals topic page (for example `/money-goals/banking/`) the filter
 * cards near the top of the page are links to sibling topic pages. The client
 * wants the page to land on the topic content (below the cards) once a visitor
 * picks a card. Because clicking a card triggers a full page navigation, we
 * cannot scroll the current page — the scroll has to happen on the destination
 * page after it loads.
 *
 * To do that without scrolling on every visit (which would hide the hero from
 * people who land here directly or refresh), we record the intent in
 * sessionStorage at click time and only act on it on the next page load. The
 * flag is cleared immediately so a manual refresh keeps the hero in view.
 *
 * @since 1.0.0
 */

/* global sessionStorage */

// Shared key for the one-shot "scroll to content" intent. sessionStorage keeps
// the value across the same-origin navigation a card click causes, and is wiped
// when the tab closes so the behavior never leaks into a future session.
const scrollIntentKey = 'gmrMoneyGoalScrollToContent';

/**
 * Wire up the card-click intent and the on-load scroll for Money Goals topics.
 *
 * @since 1.0.0
 */
export function initMoneyGoalsScroll() {
	// Scope everything to the routed Money Goals topic page so the behavior
	// never fires on other templates that happen to reuse the cards-filter
	// markup (such as the Financial Help category pages).
	const topicPage = document.querySelector( '.money-goals-topic-page' );

	if ( ! topicPage ) {
		return;
	}

	const cardsFilter = topicPage.querySelector( '.cards-filter' );

	if ( ! cardsFilter ) {
		return;
	}

	// Record the intent whenever a selector card is clicked. Both the desktop
	// grid and the mobile dropdown render the same `.cards-filter-card` links,
	// so a single query covers every variant.
	cardsFilter.querySelectorAll( '.cards-filter-card' ).forEach( ( card ) => {
		card.addEventListener( 'click', () => {
			sessionStorage.setItem( scrollIntentKey, '1' );
		} );
	} );

	// If we arrived here from a card click, scroll to the first content block
	// after the cards and clear the flag so the behavior is one-shot.
	if ( '1' === sessionStorage.getItem( scrollIntentKey ) ) {
		sessionStorage.removeItem( scrollIntentKey );

		const contentTarget = cardsFilter.nextElementSibling;

		if ( contentTarget ) {
			// Defer to the next frame so any late layout shifts (carousels,
			// images settling) are resolved before we measure the position,
			// keeping the final scroll offset accurate.
			requestAnimationFrame( () => {
				contentTarget.scrollIntoView( { behavior: 'smooth', block: 'start' } );
			} );
		}
	}
}
